import { Inject, Injectable } from '@angular/core';
import { LOCAL_STORAGE, WINDOW } from './browser-storage.tokens';

// NOTE:  Use SessionStorage to cache data in order to support multi-tab functionality for different books. 
//  Usage of LocalStorage is for sharing data across tabs only.

@Injectable({ providedIn: 'root' })
export class LocalStorage implements Storage {
    private _encryptionMode: boolean = true;

    constructor(@Inject(LOCAL_STORAGE) readonly _storage: Storage, @Inject(WINDOW) private readonly windowRef: Window) { }

    public setEncryptionMode(mode: boolean): void {
        this._encryptionMode = mode;
        this.checkEncryptionMode();
    }

    get length(): number {
        return this._storage.length;
    }

    key(index: number): string | null {
        return this._storage.key(index);
    }

    public async removeItemsOfTypeAsync(type: string) {
        const keysToRemove = Object.keys(this._storage).filter((key) => key.indexOf(type) > -1);
        for (const key of keysToRemove) {
            this._storage.removeItem(key);
        }
    }

    // Synchronous method needed to support multi-tab functionality
    public getItem(key: string) {
        if (key && key !== null && key !== undefined) {
            const prefix = this._encryptionMode ? 'g2e' : 'g2d';
            let finalKey;
            if (this.isG2RelatedItem(key) && !this.isG2NonEncryptedEntry(key) && !key.startsWith('g2')) {
                finalKey = `${prefix}.${key}`;
            } else {
                finalKey = `${key}`;
            }
            return this._storage.getItem(finalKey);
        }
    }

    // Synchronous method that DOES NOT support encryption of the key or value needed to support multi-tab functionality
    public setItem(key: string, value: any) {
        if (key && key !== null && key !== undefined) {
            const prefix = this._encryptionMode ? 'g2e' : 'g2d';
            let finalKey;
            if (this.isG2RelatedItem(key) && !this.isG2NonEncryptedEntry(key) && !key.startsWith('g2')) {
                finalKey = `${prefix}.${key}`;
            } else {
                finalKey = `${key}`;
            }
            const oldValue = this.getItem(finalKey);
            this._storage.setItem(finalKey, value);
            this.notify(finalKey, value, oldValue);
        }
    }

    // Synchronous method needed to support multi-tab functionality
    public removeItem(key: string) {
        if (key && key !== null && key !== undefined) {
            const prefix = this._encryptionMode ? 'g2e' : 'g2d';
            let finalKey;
            if (this.isG2RelatedItem(key) && !this.isG2NonEncryptedEntry(key) && !key.startsWith('g2')) {
                finalKey = `${prefix}.${key}`;
            } else {
                finalKey = `${key}`;
            }

            const storageKeys = Object.keys(this._storage);

            if (storageKeys.indexOf(finalKey) !== -1) {
                const oldValue = this.getItem(finalKey);
                this._storage.removeItem(finalKey);
                this.notify(finalKey, null, oldValue);
            } else {
                console.log(
                    `LocalStorageService tried to get value of key: ${key} using finalKey of: ${finalKey} but value was null`
                );
            }
        }
    }

    public clear() {
        this._storage.clear();
        this.notify(null, null, null);
    }

    public removeItemOfType(type: string) {
        const keysToRemove = Object.keys(this._storage).filter((key) => key.indexOf(type) > -1);
        for (const key of keysToRemove) {
            this._storage.removeItem(key);
        }
    }

    public getAllKeys() {
        return Object.keys(this._storage);
    }

    public getAllG2Keys(): string[] {
        let g2Keys: string[] = [];
        const storageKeys = Object.keys(this._storage);
        if (storageKeys.length > 0) {
            g2Keys = storageKeys
                .filter((key) => this.isG2RelatedItem(key))
                .map((key) => (key.startsWith('g2') ? key.slice(4) : key));
        }
        return g2Keys;
    }

    public getAllTabKeys() {
        let tabKeys: string[] = [];
        const storageKeys = Object.keys(this._storage);
        if (storageKeys.length > 0) {
            tabKeys = storageKeys.filter((key) => key.startsWith('suremgatab'));
        }

        return tabKeys;
    }

    private checkEncryptionMode() {
        let allKeys: string[] = [];
        allKeys = Object.keys(this._storage);
        let itemsToRemove: string[] = [];

        if (allKeys.length > 0) {
            if (this._encryptionMode) {
                itemsToRemove = allKeys.filter((key) => this.isG2RelatedItem(key)).filter((key) => !key.startsWith('g2e'));
            } else {
                itemsToRemove = allKeys.filter((key) => this.isG2RelatedItem(key)).filter((key) => !key.startsWith('g2d'));
            }

            if (itemsToRemove.length > 0) {
                for (const key of itemsToRemove) {
                    if (!this.isG2NonEncryptedEntry(key)) {
                        this._storage.removeItem(key);
                    }
                }
            }
        }
    }

    private isG2RelatedItem(key: string) {
        if (key && key !== null && key !== undefined) {
            if (key && (key.startsWith('persist:viewer') || key.startsWith('toolData-'))) {
                return false;
            }
            return true;
        }
        return false;
    }

    private isG2NonEncryptedEntry(key: string) {
        if (key && key !== null && key !== undefined) {
            let k = key.toLocaleLowerCase();
            if (
                k.startsWith('suremgatab') ||
                k.endsWith('configupdatetimestamp') ||
                k.startsWith('currentuser') ||
                k.startsWith('preference:user') ||
                k.startsWith('tab:') ||
                k.startsWith('username')
            ) {
                return true;
            }
            return false;
        }
        return false;
    }

    private notify(key: string | null, newValue: string | null, oldValue: string | null) {
        const event = new StorageEvent('storage', {
            key,
            newValue,
            oldValue,
            storageArea: this._storage,
            url: this.windowRef.location.href
        });

        this.windowRef.dispatchEvent(event);
    }
}
